import React, { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { useApolloClient } from '@apollo/client';

const ErrorComponent = ({ error, componentStack, resetError, componentName, parentName, ...additionalProps }) => {
    const apolloClient = useApolloClient();
    
    useEffect(() => {
        console.log('🐛 ErrorComponent Debug - All Props:', {
            error,
            componentStack,
            resetError: typeof resetError,
            componentName,
            parentName,
            additionalProps,
            additionalPropsKeys: Object.keys(additionalProps),
            additionalPropsValues: Object.entries(additionalProps).map(([key, value]) => ({
                key,
                type: typeof value,
                value: value,
                isUndefined: value === undefined,
                isNull: value === null
            }))
        });
    }, [error, componentStack, resetError, componentName, parentName, additionalProps]);

    useEffect(() => {
        // Single point of error capture with comprehensive context
        Sentry.withScope((scope) => {
            scope.setLevel('fatal'); // Use 'fatal' for critical errors
            
            // Add all relevant context
            scope.setExtra('componentStack', componentStack);
            scope.setExtra('errorLocation', window.location.href);
            scope.setExtra('timestamp', new Date().toISOString());
            scope.setTag('error_type', 'react_error_boundary');
            scope.setTag('error_category', 'critical_render_failure');
            scope.setTag('manually_captured', true);
            
            // Add component-specific context if provided
            if (componentName) {
                scope.setTag('component', componentName);
                scope.setTag('failed_component', componentName); // Make it more visible
            }
            if (parentName) {
                scope.setTag('parent', parentName);
            }
            
            // Enhanced Apollo Client Context
            if (apolloClient) {
                try {
                    // Get recent query information from Apollo Client cache
                    const cache = apolloClient.cache;
                    const networkStatus = apolloClient.networkStatus;
                    
                    // Get active query information
                    const queryManager = apolloClient.queryManager;
                    if (queryManager) {
                        const activeQueries = Array.from(queryManager.queries.values())
                            .filter(query => query.networkStatus !== 7) // Not ready/complete
                            .map(query => ({
                                operationName: query.options?.query?.definitions?.[0]?.name?.value,
                                variables: query.variables,
                                networkStatus: query.networkStatus,
                                errorPolicy: query.options?.errorPolicy,
                                fetchPolicy: query.options?.fetchPolicy
                            }));

                        if (activeQueries.length > 0) {
                            scope.setContext('apollo_active_queries', {
                                count: activeQueries.length,
                                queries: activeQueries.slice(0, 5) // Limit to prevent payload bloat
                            });
                        }
                    }

                    // Check for Apollo errors in the cache/client
                    const apolloErrors = [];
                    
                    // Try to extract recent errors from Apollo's internal state
                    if (queryManager?.queries) {
                        queryManager.queries.forEach((queryInfo, queryId) => {
                            if (queryInfo.networkError || queryInfo.graphQLErrors?.length > 0) {
                                apolloErrors.push({
                                    queryId: queryId,
                                    operationName: queryInfo.options?.query?.definitions?.[0]?.name?.value,
                                    networkError: queryInfo.networkError ? {
                                        message: queryInfo.networkError.message,
                                        statusCode: queryInfo.networkError.statusCode
                                    } : null,
                                    graphQLErrors: queryInfo.graphQLErrors?.map(err => ({
                                        message: err.message,
                                        path: err.path,
                                        extensions: err.extensions
                                    })) || []
                                });
                            }
                        });
                    }

                    if (apolloErrors.length > 0) {
                        scope.setContext('apollo_recent_errors', {
                            errors: apolloErrors.slice(0, 3) // Limit to most recent 3
                        });
                        scope.setTag('has_apollo_errors', true);
                    }

                    // Add Apollo Client configuration info
                    scope.setContext('apollo_client_config', {
                        uri: apolloClient.link?.options?.uri || 'unknown',
                        defaultFetchPolicy: apolloClient.defaultOptions?.query?.fetchPolicy,
                        defaultErrorPolicy: apolloClient.defaultOptions?.query?.errorPolicy,
                        cacheSize: Object.keys(cache.data?.data || {}).length
                    });

                } catch (apolloError) {
                    console.warn('Failed to extract Apollo context for Sentry:', apolloError);
                    scope.setExtra('apollo_context_error', apolloError.message);
                }
            }

            // Check if this is an Apollo-related error
            const isApolloError = error?.name === 'ApolloError' || 
                                error?.message?.includes('ApolloError') ||
                                error?.networkError ||
                                error?.graphQLErrors;

            if (isApolloError) {
                scope.setTag('apollo_error', true);
                scope.setTag('error_source', 'apollo_client');

                // Extract Apollo-specific error details
                if (error.networkError) {
                    scope.setContext('apollo_network_error', {
                        message: error.networkError.message,
                        statusCode: error.networkError.statusCode,
                        bodyText: error.networkError.bodyText
                    });
                }

                if (error.graphQLErrors?.length > 0) {
                    scope.setContext('apollo_graphql_errors', {
                        errors: error.graphQLErrors.map(err => ({
                            message: err.message,
                            locations: err.locations,
                            path: err.path,
                            extensions: err.extensions
                        }))
                    });
                }

                // Enhanced fingerprinting for Apollo errors
                scope.setFingerprint([
                    'react_error_boundary',
                    'apollo_error',
                    componentName || 'unknown_component',
                    error.networkError ? 'network_error' : 'graphql_error',
                    error.message?.slice(0, 50) || 'unknown'
                ]);
            } else {
                // Set a clear fingerprint for these critical errors
                scope.setFingerprint([
                    'react_error_boundary',
                    componentName || 'unknown_component',
                    error?.name || 'Error',
                    error?.message?.slice(0, 50) || 'unknown'
                ]);
            }
            
            // Try to extract more detailed error location from component stack
            if (componentStack) {
                const stackLines = componentStack.split('\n');
                // Find the first non-ErrorBoundary component
                const errorComponent = stackLines.find(line => 
                    !line.includes('ErrorBoundary') && 
                    !line.includes('Suspense') &&
                    line.trim().startsWith('at ')
                );
                if (errorComponent) {
                    scope.setExtra('error_component_from_stack', errorComponent.trim());
                }
            }
            
            // Try to extract function/hook name from error stack
            if (error?.stack) {
                const stackLines = error.stack.split('\n');
                
                // Look for hook calls (use*, handle*, on*)
                const hookLine = stackLines.find(line => 
                    /\b(use[A-Z]\w*|handle\w*|on[A-Z]\w*)\b/.test(line)
                );
                if (hookLine) {
                    const hookMatch = hookLine.match(/\b(use[A-Z]\w*|handle\w*|on[A-Z]\w*)\b/);
                    if (hookMatch) {
                        scope.setTag('hook_or_handler', hookMatch[1]);
                    }
                }
                
                // Extract the most relevant function name (first function after the error)
                const functionMatch = stackLines[1]?.match(/at\s+(\w+)\s*\(/);
                if (functionMatch && functionMatch[1] !== 'Object') {
                    scope.setTag('error_function', functionMatch[1]);
                }

                // Look for Apollo-specific functions in the stack
                const apolloStackLine = stackLines.find(line => 
                    line.includes('apollo') || 
                    line.includes('ApolloError') ||
                    line.includes('ObservableQuery') ||
                    line.includes('QueryManager')
                );
                if (apolloStackLine) {
                    scope.setExtra('apollo_stack_trace', apolloStackLine.trim());
                }
            }
            
            // Add any additional props as context
            const extraProps = Object.entries(additionalProps).reduce((acc, [key, value]) => {
                if (typeof value !== 'function' && key !== 'children') {
                    acc[key] = value;
                }
                return acc;
            }, {});
            
            if (Object.keys(extraProps).length > 0) {
                scope.setContext('additional_props', extraProps);
            }
            
            // Capture the exception
            Sentry.captureException(error, {
                tags: {
                    component_failure: true,
                    requires_immediate_attention: true
                }
            });
        });
        
        // Also log to console for local debugging with enhanced Apollo info
        console.error('React Error Boundary caught error:', {
            error,
            component: componentName,
            parent: parentName,
            componentStack,
            extraProps: additionalProps,
            isApolloError: error?.name === 'ApolloError',
            apolloClientState: apolloClient ? {
                hasActiveQueries: apolloClient.queryManager?.queries?.size > 0,
                networkStatus: apolloClient.networkStatus
            } : null
        });
    }, [error, componentStack, componentName, parentName, apolloClient]);

    const handleReset = () => {
        try {
            console.log('Resetting application and clearing storage...');
            
            // Clear Apollo cache if available
            if (apolloClient) {
                try {
                    apolloClient.clearStore();
                    console.log('Apollo cache cleared');
                } catch (apolloClearError) {
                    console.warn('Failed to clear Apollo cache:', apolloClearError);
                }
            }
            
            // Try to reset the error boundary first
            if (resetError && typeof resetError === 'function') {
                resetError();
            }
        } catch (resetError) {
            console.error('Failed to reset error boundary:', resetError);
        }
    };

    return (
        <div className='headless-error-component'>
            <div className='error-component-title'>
                Problem loading{componentName ? ` ${componentName}` : ''}:
            </div>
            <div role='button' onClick={handleReset} className='error-reset-button'>
                click to retry.
            </div>
        </div>
    );
};

export default ErrorComponent;
